import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { MediaUpload, MediaPreview } from '../components/MediaUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Pencil, Plus, Trash2, Save, Dumbbell, Image, Video, Settings, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { toast } from 'sonner';

// Vidéo de démonstration au niveau du BLOC (une vidéo par groupe d'exercices)
function SectionVideoButton({ prog, section, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const saveVideo = async (videoUrl) => {
    const sections = (prog.sections || []).map(s => s.exo_num === section.exo_num ? { ...s, video: videoUrl } : s);
    await apiClient.put(`/fixed-programs/${prog.group_num}`, { ...prog, sections });
    onSaved();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Vidéo trop volumineuse (max 100MB)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post('/programs/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await saveVideo(`/api/videos/${data.path}`);
      toast.success(`Vidéo du bloc EXO ${section.exo_num} ajoutée !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload vidéo');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Supprimer la vidéo de ce bloc ?')) return;
    setBusy(true);
    try {
      await saveVideo(null);
      toast.success('Vidéo supprimée');
    } catch { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <span className="flex items-center gap-1">
      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/mpeg" onChange={handleFile} className="hidden" />
      {section.video ? (
        <>
          <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-sm font-bold">🎬 Vidéo</span>
          <button onClick={handleRemove} disabled={busy} className="p-1 text-zinc-500 hover:text-red-400" title="Supprimer la vidéo" data-testid={`section-video-remove-${prog.group_num}-${section.exo_num}`}>
            <Trash2 className="w-3 h-3" />
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="p-1 text-zinc-500 hover:text-[#FF6B35]" title="Remplacer la vidéo">
            <Pencil className="w-3 h-3" />
          </button>
        </>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}
          className="border-red-500/40 text-red-400 h-7 text-[10px] hover:bg-red-500/10"
          data-testid={`section-video-add-${prog.group_num}-${section.exo_num}`}>
          <Video className="w-3 h-3 mr-0.5" /> {busy ? '...' : 'Vidéo'}
        </Button>
      )}
    </span>
  );
}

// Vidéo au niveau du PROGRAMME (une vidéo pour toute la séance, ex: Dos — Lundi)
function ProgramVideoButton({ prog, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const saveVideo = async (videoUrl) => {
    await apiClient.put(`/fixed-programs/${prog.group_num}`, { ...prog, program_video: videoUrl });
    onSaved();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Vidéo trop volumineuse (max 100MB)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post('/programs/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await saveVideo(`/api/videos/${data.path}`);
      toast.success(`Vidéo du programme ${prog.muscle_group} — ${prog.day} ajoutée !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload vidéo');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Supprimer la vidéo du programme ?')) return;
    setBusy(true);
    try {
      await saveVideo(null);
      toast.success('Vidéo supprimée');
    } catch { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0F0F0F] border border-[#FF6B35]/30 rounded-sm p-3">
      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/mpeg" onChange={handleFile} className="hidden" />
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-[#FF6B35] flex items-center gap-1.5">
          <Video className="w-3.5 h-3.5" /> Vidéo du programme
        </span>
        {prog.program_video ? (
          <span className="flex items-center gap-1">
            <button onClick={handleRemove} disabled={busy} className="p-1 text-zinc-500 hover:text-red-400" title="Supprimer la vidéo" data-testid={`program-video-remove-${prog.group_num}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="p-1 text-zinc-500 hover:text-[#FF6B35]" title="Remplacer la vidéo" data-testid={`program-video-replace-${prog.group_num}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </span>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}
            className="border-[#FF6B35]/40 text-[#FF6B35] h-7 text-[10px] hover:bg-[#FF6B35]/10"
            data-testid={`program-video-add-${prog.group_num}`}>
            <Plus className="w-3 h-3 mr-0.5" /> {busy ? 'Envoi...' : 'Ajouter la vidéo'}
          </Button>
        )}
      </div>
      {prog.program_video ? (
        <video src={prog.program_video} controls preload="metadata" playsInline
          className="w-full max-h-48 rounded-sm border border-[#FF6B35]/30 bg-black object-contain mt-2"
          data-testid={`program-video-preview-${prog.group_num}`} />
      ) : (
        <p className="text-[10px] text-zinc-500 mt-1.5">Une vidéo pour toute la séance, visible par les membres en haut du programme (MP4, max 100MB)</p>
      )}
    </div>
  );
}

export default function EditProgramPage() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [editExercise, setEditExercise] = useState(null); // {groupNum, exoNum, idx, data}
  const [newExercise, setNewExercise] = useState(null); // {groupNum, exoNum}
  const [pointsConfig, setPointsConfig] = useState(null);
  const [showPoints, setShowPoints] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [progRes, ptsRes] = await Promise.all([
        apiClient.get('/fixed-programs'),
        apiClient.get('/points/config')
      ]);
      setPrograms(progRes.data);
      setPointsConfig(ptsRes.data);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateExercise = async () => {
    if (!editExercise) return;
    try {
      await apiClient.put(
        `/fixed-programs/${editExercise.groupNum}/section/${editExercise.exoNum}/exercise/${editExercise.idx}`,
        editExercise.data
      );
      toast.success('Exercice mis à jour');
      setEditExercise(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddExercise = async () => {
    if (!newExercise) return;
    try {
      await apiClient.post(
        `/fixed-programs/${newExercise.groupNum}/section/${newExercise.exoNum}/exercise`,
        newExercise.data
      );
      toast.success('Exercice ajouté');
      setNewExercise(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteExercise = async (groupNum, exoNum, idx) => {
    if (!window.confirm('Supprimer cet exercice ?')) return;
    try {
      await apiClient.delete(`/fixed-programs/${groupNum}/section/${exoNum}/exercise/${idx}`);
      toast.success('Exercice supprimé');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleSavePoints = async () => {
    try {
      await apiClient.put('/points/config', pointsConfig);
      toast.success('Points mis à jour');
      setShowPoints(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <Shield className="w-16 h-16 text-zinc-700" />
        <BottomNav />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const POINTS_LABELS = {
    training_completed: 'Entraînement réalisé',
    challenge_completed: 'Challenge complété',
    weekly_streak: 'Régularité hebdo',
    community_post: 'Post communauté',
    community_like_received: 'Like reçu',
    deal_shared: 'Bon plan partagé',
    photo_shared: 'Photo partagée',
    perfect_attendance: 'Présence parfaite'
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-20">
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-bold text-white">Éditer les programmes</h1>
            <p className="text-xs text-zinc-500">Modifier exercices, séries, médias</p>
          </div>
          <Button onClick={() => setShowPoints(true)} variant="outline" className="border-zinc-700 text-zinc-400" size="sm" data-testid="points-config-btn">
            <Settings className="w-4 h-4 mr-1" /> Points
          </Button>
        </div>
      </header>

      <main className="p-4 space-y-3 animate-fade-in">
        {programs.map((prog) => {
          const isExpanded = expandedGroup === prog.group_num;
          return (
            <div key={prog.group_num} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden">
              <button onClick={() => setExpandedGroup(isExpanded ? null : prog.group_num)} className="w-full flex items-center gap-3 p-4 text-left">
                <div className="w-8 h-8 rounded-sm bg-[#FF6B35]/20 flex items-center justify-center text-sm font-bold text-[#FF6B35]">{prog.group_num}</div>
                <div className="flex-1">
                  <p className="text-white font-bold text-sm">{prog.muscle_group} — {prog.day}</p>
                  <p className="text-xs text-zinc-400">{prog.sections?.length || 0} sections</p>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              </button>

              {isExpanded && (
                <div className="border-t border-[#2A2A2E] p-3 space-y-3">
                  <ProgramVideoButton prog={prog} onSaved={fetchData} />
                  {(prog.sections || []).map((section) => (
                    <div key={section.exo_num} className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-[#FF6B35]">EXO {section.exo_num} — {section.title}</span>
                        <div className="flex items-center gap-1.5">
                          <SectionVideoButton prog={prog} section={section} onSaved={fetchData} />
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 text-zinc-400 h-7 text-[10px]"
                            onClick={() => setNewExercise({
                              groupNum: prog.group_num,
                              exoNum: section.exo_num,
                              data: { name: '', sets: '4', reps: '12', weight: '-', rest: '60s', target: '', media: [] }
                            })}
                          >
                            <Plus className="w-3 h-3 mr-0.5" /> Exo
                          </Button>
                        </div>
                      </div>
                      {section.video && (
                        <video src={section.video} controls preload="metadata" playsInline className="w-full max-w-xs h-32 rounded-sm border border-red-500/30 bg-black object-contain mb-2" data-testid={`section-video-preview-${prog.group_num}-${section.exo_num}`} />
                      )}
                      {(section.exercises || []).map((ex, idx) => (
                        <div key={`${ex.name}-${idx}`} className="py-2 border-b border-[#2A2A2E]/30 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-400 w-4">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-zinc-300 truncate">{ex.name}</p>
                              <p className="text-xs text-zinc-400">{ex.sets}x{ex.reps} · {ex.weight}{ex.target ? ` · ${ex.target}` : ''}</p>
                            </div>
                            {(ex.media || []).length > 0 && (
                              <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-sm">{ex.media.length} média{ex.media.length > 1 ? 's' : ''}</span>
                            )}
                            <button
                              onClick={() => setEditExercise({ groupNum: prog.group_num, exoNum: section.exo_num, idx, data: { ...ex } })}
                              className="p-1 text-zinc-500 hover:text-[#FF6B35]"
                              data-testid={`edit-ex-${prog.group_num}-${section.exo_num}-${idx}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteExercise(prog.group_num, section.exo_num, idx)}
                              className="p-1 text-zinc-500 hover:text-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {/* Media thumbnails */}
                          {(ex.media || []).length > 0 && (
                            <div className="flex gap-1.5 mt-1.5 ml-6">
                              {ex.media.slice(0, 4).map((url, mi) => {
                                const isVideo = url.includes('youtube') || url.includes('youtu.be') || url.includes('/api/videos/') || /\.(mp4|webm|mov)(\?|$)/i.test(url);
                                return isVideo ? (
                                  <div key={`${url}-${mi}`} className="w-12 h-12 rounded-sm bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                                    <Video className="w-4 h-4 text-red-400" />
                                  </div>
                                ) : (
                                  <img key={`${url}-${mi}`} src={url} alt="" className="w-12 h-12 rounded-sm object-cover border border-zinc-700" />
                                );
                              })}
                              {ex.media.length > 4 && (
                                <div className="w-12 h-12 rounded-sm bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] text-zinc-400">+{ex.media.length - 4}</div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </main>

      {/* Edit Exercise Dialog */}
      <Dialog open={!!editExercise} onOpenChange={() => setEditExercise(null)}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Modifier l'exercice</DialogTitle></DialogHeader>
          {editExercise && (
            <div className="space-y-3">
              <div><Label className="text-zinc-400 text-xs">Nom</Label><Input value={editExercise.data.name || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, name: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-zinc-500 text-[10px]">Séries</Label><Input value={editExercise.data.sets || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, sets: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Reps</Label><Input value={editExercise.data.reps || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, reps: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Repos</Label><Input value={editExercise.data.rest || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, rest: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-500 text-[10px]">Poids</Label><Input value={editExercise.data.weight || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, weight: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Tempo</Label><Input value={editExercise.data.tempo || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, tempo: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              </div>
              <div><Label className="text-zinc-400 text-xs">Cible musculaire</Label><Input value={editExercise.data.target || ''} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, target: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              <div>
                <Label className="text-zinc-400 text-xs flex items-center gap-1"><Image className="w-3 h-3" /> Photos / Vidéos</Label>
                <MediaUpload onMediaAdded={url => setEditExercise({...editExercise, data: {...editExercise.data, media: [...(editExercise.data.media || []), url]}})} />
                <MediaPreview urls={editExercise.data.media || []} onRemove={i => setEditExercise({...editExercise, data: {...editExercise.data, media: (editExercise.data.media || []).filter((_, idx) => idx !== i)}})} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editExercise.data.superset || false} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, superset: e.target.checked}})} className="accent-[#FF6B35]" />
                <span className="text-xs text-zinc-400">Superset</span>
                <input type="checkbox" checked={editExercise.data.is_finisher || false} onChange={e => setEditExercise({...editExercise, data: {...editExercise.data, is_finisher: e.target.checked}})} className="accent-red-500 ml-4" />
                <span className="text-xs text-zinc-400">Finisher HIIT</span>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setEditExercise(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
                <Button onClick={handleUpdateExercise} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]"><Save className="w-4 h-4 mr-1" /> Sauver</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Exercise Dialog */}
      <Dialog open={!!newExercise} onOpenChange={() => setNewExercise(null)}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Ajouter un exercice</DialogTitle></DialogHeader>
          {newExercise && (
            <div className="space-y-3">
              <div><Label className="text-zinc-400 text-xs">Nom *</Label><Input value={newExercise.data.name} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, name: e.target.value}})} placeholder="Curl barre EZ" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-zinc-500 text-[10px]">Séries</Label><Input value={newExercise.data.sets} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, sets: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Reps</Label><Input value={newExercise.data.reps} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, reps: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Repos</Label><Input value={newExercise.data.rest} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, rest: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-500 text-[10px]">Poids</Label><Input value={newExercise.data.weight} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, weight: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
                <div><Label className="text-zinc-500 text-[10px]">Cible</Label><Input value={newExercise.data.target} onChange={e => setNewExercise({...newExercise, data: {...newExercise.data, target: e.target.value}})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs flex items-center gap-1"><Image className="w-3 h-3" /> Photos / Vidéos</Label>
                <MediaUpload onMediaAdded={url => setNewExercise({...newExercise, data: {...newExercise.data, media: [...(newExercise.data.media || []), url]}})} />
                <MediaPreview urls={newExercise.data.media || []} onRemove={i => setNewExercise({...newExercise, data: {...newExercise.data, media: (newExercise.data.media || []).filter((_, idx) => idx !== i)}})} />
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setNewExercise(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
                <Button onClick={handleAddExercise} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Points Config Dialog */}
      <Dialog open={showPoints} onOpenChange={setShowPoints}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Configuration des points</DialogTitle></DialogHeader>
          {pointsConfig && (
            <div className="space-y-3">
              {Object.entries(POINTS_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">{label}</span>
                  <Input
                    type="number"
                    value={pointsConfig[key] || 0}
                    onChange={e => setPointsConfig({...pointsConfig, [key]: parseInt(e.target.value) || 0})}
                    className="w-20 bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm text-center h-8"
                  />
                </div>
              ))}
              <DialogFooter className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPoints(false)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
                <Button onClick={handleSavePoints} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]"><Save className="w-4 h-4 mr-1" /> Sauver</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
